import { useCallback, useRef, useState } from "react";
import { friendlyMessage, lookupUsers, type GetToken } from "./accountApi";
import type { UserView } from "../models/account";

/** Why an id has no name yet — the UI must not render these three the same way. */
export type UserStatus =
  /** Resolved: we have a name/email for this id. */
  | "resolved"
  /** The server answered but omitted this id — deleted, or outside the caller's privacy scope. */
  | "unknown"
  /** The lookup call itself failed (network/5xx/auth). Retriable — NOT the same as "unknown". */
  | "failed"
  /** Not resolved yet: in flight, or never asked for. */
  | "pending";

// Resolves user ids to display info (name / affiliation / email) so the UI never has to show raw
// user ids. Caches resolved views, batch-fetches unknown ids via POST /api/users/lookup, and
// negative-caches ids the SERVER declined to return. Call ensure(ids) from an effect when the
// visible id set changes; render with label(id) / detail(id).
//
// A failed lookup is deliberately NOT negative-cached: swallowing it would silently degrade every
// member/assignee/author cell back to a short id with no indication anything is wrong — on a
// regulated screen, "I can't tell who approved this" and "the server is down" must not look alike.
// Failures surface as `error` (+ statusOf(id) === "failed") and stay retriable via retry().
export function useUserDirectory(getToken: GetToken) {
  const [map, setMap] = useState<Record<string, UserView>>({});
  const mapRef = useRef(map);
  mapRef.current = map;
  const inflight = useRef<Set<string>>(new Set());
  // `missing` and `failed` are state, not refs: a cell renders "확인할 수 없는 사용자" vs "이름 조회
  // 실패" vs still-loading off them, so learning an id's fate has to re-render. As a ref, a batch
  // that resolved to NOTHING (every id deleted/out of scope) would update no state at all and those
  // cells would sit on "loading" forever.
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const addAll = (setter: (fn: (cur: Set<string>) => Set<string>) => void, ids: string[]) => {
    if (ids.length === 0) return;
    setter((cur) => {
      const next = new Set(cur);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const fetchIds = useCallback((need: string[]) => {
    if (need.length === 0) return;
    need.forEach((id) => inflight.current.add(id));
    lookupUsers(getToken, need)
      .then((views) => {
        const returned = new Set(views.map((v) => v.id));
        // Asked for but not returned → the server knows and declined: deleted or out of scope.
        addAll(setMissing, need.filter((id) => !returned.has(id)));
        if (views.length > 0) {
          setMap((cur) => {
            const next = { ...cur };
            for (const v of views) next[v.id] = v;
            return next;
          });
        }
      })
      .catch((e) => {
        addAll(setFailed, need);
        setError(friendlyMessage(e));
      })
      .finally(() => need.forEach((id) => inflight.current.delete(id)));
  }, [getToken]);

  const ensure = useCallback((ids: (string | null | undefined)[]) => {
    const all = [...new Set(ids.filter((x): x is string => !!x))];
    fetchIds(all.filter((id) =>
      !mapRef.current[id] && !inflight.current.has(id) && !missing.has(id) && !failed.has(id)));
  }, [fetchIds, missing, failed]);

  /** Re-attempt every id whose lookup failed. Clears the error so the banner disappears on success. */
  const retry = useCallback(() => {
    const again = [...failed];
    setFailed(new Set());
    setError(null);
    fetchIds(again.filter((id) => !mapRef.current[id] && !inflight.current.has(id)));
  }, [failed, fetchIds]);

  const get = useCallback((id: string | null | undefined) => (id ? map[id] : undefined), [map]);

  const statusOf = useCallback((id: string | null | undefined): UserStatus => {
    if (!id) return "unknown";
    if (map[id]) return "resolved";
    if (failed.has(id)) return "failed";
    if (missing.has(id)) return "unknown";
    return "pending";
  }, [map, failed, missing]);

  /** Short human label — name, else email, else a short id fallback. */
  const label = useCallback((id: string | null | undefined) => {
    if (!id) return "";
    const v = map[id];
    return v?.displayName || v?.email || `${id.slice(0, 8)}…`;
  }, [map]);

  /** Fuller "이름 · 소속 · 이메일" descriptor for member rows/pickers. */
  const detail = useCallback((id: string | null | undefined) => {
    if (!id) return "";
    const v = map[id];
    if (!v) return `${id.slice(0, 8)}…`;
    const bits: string[] = [v.displayName || `${id.slice(0, 8)}…`];
    if (v.affiliation) bits.push(v.affiliation);
    if (v.email) bits.push(v.email);
    return bits.join(" · ");
  }, [map]);

  return { ensure, get, label, detail, statusOf, error, retry };
}
